<template>
  <div
    id="nav_bar"
    ref="navBarRef"
    :style="{
      'background-color': isLightSurface
        ? 'rgba(241, 241, 241, 0.95)'
        : 'rgba(241, 241, 241, 0)',
      borderBottom: isLightSurface
        ? '1px solid #c3c3c3'
        : '1px solid transparent',
    }"
  >
    <div class="content">
      <div class="logo">
        <img
          :src="isLightSurface ? require('@/assets/logo_band.png') : require('@/assets/logo_white.png')"
          alt="ALC(Air & Liquid Cooling Technology Limited (HK))"
          @click="onBack"
        />
      </div>
      <div 
      class="routes" 
      v-if="!shrink"
      :style="{
        'color': isLightSurface ? '#3e3e3e' : '#f2f2f2'
    }"
      >
        <div
          v-for="route in routes"
          :key="route.title"
          @click="router.push(route.path)"
          @mouseenter="showPopover = true"
          :class="isRouteActive(route.path) ? 'active' : 'route'"
        >
          <DropdownBar :options="route.children">
            <div class="context">
              <span class="route_label">{{ route.title }}</span>
              <van-icon
                name="arrow-down"
                v-if="route.children"
                size="10px"
                class="arrow"
              />
            </div>
          </DropdownBar>
        </div>

        <div class="toggle_button" @click="onClickButton">
          {{ $t("navbar.PORTAL") }}
        </div>
      </div>
      <div class="toggle" v-else>
        <van-icon name="wap-nav" size="3rem" :color="isLightSurface ? '#000' : '#fff'" @click="onToggle" />
      </div>
      <DropdownSheet
        v-if="visible"
        @onClose="visible = false"
        :routes="routes"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import {
  computed,
  ComputedRef,
  inject,
  onMounted,
  ref,
  onUnmounted,
} from "vue";
import { useRouter } from "vue-router";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { gsap } from "gsap";
import provide_key from "@/constant/provide_key";
import DropdownSheet from "./DropdownSheet.vue";
import useToggleLanguage from "@/utils/useToggleLanguage";
import routeHash from "@/constant/router_hash";
import DropdownBar from "./DropdownBar.vue";

gsap.registerPlugin(ScrollTrigger);
const { t } = useI18n();
const router = useRouter();
const visible = ref<boolean>(false);


interface routeChildType {
  path: string;
  title: string;
}

interface routeType {
  path: string;
  title: string;
  children?: routeChildType[];
  showPopover?: boolean;
}

const showPopover = ref(false);
const scrollTop = ref(0);
const navBarRef = ref<null | HTMLElement>(null);

const scrollToUpdate = computed(() => {
  if (navBarRef.value) {
    return navBarRef.value.clientHeight < scrollTop.value;
  }
  return false;
});

const isLightSurface = computed(
  () =>
    scrollToUpdate.value ||
    router.currentRoute.value.path === "/projects" ||
    router.currentRoute.value.path.startsWith("/news")
);
const hoverColor = computed(() => isLightSurface.value ? '#002fa5' : '#a3a3a3')
const borderBottomColor = computed(() => isLightSurface.value ? '#002fa5' : '#fff')
const isRouteActive = (path: string) =>
  router.currentRoute.value.path === path ||
  (path !== "/" && router.currentRoute.value.path.startsWith(`${path}/`));

const routes: ComputedRef<routeType[]> = computed(() => [
  {
    path: "/",
    title: t("navbar.HOME"),
  },
  {
    path: "/solution",
    title: t("navbar.SOLUTION"),
    children: [
      {
        path: routeHash.AIR_TO_LIQUID_COOLING,
        title: t("footer.WIND_TO_LIQUID"),
      },
      {
        path: routeHash.SYSTEMIC_LIQUID_COOLING,
        title: t("footer.LIQUID_COOLING"),
      },
      {
        path: routeHash.RDHX_AIR_COOLED,
        title: t("footer.LIQUID_COOLING_REAR_DOOR"),
      },
    ],
  },
  {
    path: "/service",
    title: t("navbar.SERVICE"),
    children: [
      {
        path: routeHash.IDC_LIQUID_COOLING,
        title: t("footer.DATA_CENTER"),
      },
      {
        path: routeHash.AIGC_INFRASTRUCTURE,
        title: t("footer.COMPUTATIONAL_EQUIPMENT"),
      },
    ],
  },
  {
    path: "/product",
    title: t("navbar.PRODUCT"),
    children: [
      {
        path: routeHash.L2L_PRODUCT,
        title: t("product.L2L_NAV"),
      },
      {
        path: routeHash.L2A_PRODUCT,
        title: t("product.L2A_NAV"),
      },
      {
        path: routeHash.RDHx_PRODUCT,
        title: t("product.RDHx_NAV"),
      },
      {
        path: routeHash.OTHERS_PRODUCT,
        title: t("product.OTHERS_NAV"),
      },
    ],
  },
  {
    path: "/projects",
    title: t("navbar.PROJECTS"),
  },
  {
    path: "/news",
    title: t("navbar.NEWS"),
    children: [
      {
        path: "/news/selection",
        title: t("navbar.SELECTION_NOTICE"),
      },
      {
        path: "/news/award",
        title: t("navbar.AWARD_NOTICE"),
      },
    ],
  },
  {
    path: "/company",
    title: t("navbar.COMPANY"),
  },
]);

const shrink = inject(provide_key.SHRINK);
const { onToggleLanguage } = useToggleLanguage();
const onToggle = () => (visible.value = !visible.value);
const onBack = () => router.push("/");
const onClickButton = () => {
  // location.reload()
  onToggleLanguage()
}

const scrollerEven = () => (scrollTop.value = window.scrollY);

onMounted(() => {
  window.addEventListener("scroll", scrollerEven);
});

onUnmounted(() => {
  window.removeEventListener("scroll", scrollerEven);
});

</script>

<style lang="less" scoped>
#nav_bar {
  width: 100%;
  height: 4rem;
  overflow: hidden;
  background: rgba(241, 241, 241, 0);
  border-bottom: 1px solid transparent;
  display: flex;
  align-items: center;
  position: fixed;
  top: 0;
  z-index: 9;
  padding: 0 2rem;

  .content {
    margin: 0 auto;
    max-width: calc(var(--view-max-width) + 1rem);
    display: flex;
    justify-content: space-between;
    width: 100%;
    height: 100%;
  }

  .logo {
    cursor: pointer;
    width: 9rem;
    overflow: hidden;
    height: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .routes {
    display: flex;
    gap: 1rem;
    cursor: pointer;
    color: var(--font-color-white-strong);
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-big);
    align-items: center;
    overflow-y: hidden;

    .route {
      transition: transform 0.3s ease;
      display: flex;
      gap: 3px;
      height: 4rem;
      overflow-y: hidden;

      .route_label {
        border-bottom: 2px solid transparent;
      }

      .context {
        display: flex;
        align-items: center;
        gap: 3px;
        height: 100%;
      }

      &:hover {
        color: v-bind(hoverColor);

        .arrow {
          transform: rotate(180deg);
          transition: all 0.5s;
        }
      }
    }

    .active {
      display: flex;
      gap: 3px;
      height: 100%;
      overflow-y: hidden;

      .route_label {
        border-bottom: 2px solid v-bind(borderBottomColor);
      }

      .context {
        display: flex;
        align-items: center;
        gap: 3px;
      }

      &:hover {
        color: v-bind(hoverColor);

        .route_label {
          border-bottom: 2px solid v-bind(hoverColor);
        }

        .arrow {
          transform: rotate(180deg);
          transition: all 0.5s;
        }
      }
    }

    .toggle_button {
      background: #fff;
      box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
      padding: 0.4rem 0.6rem;
      color: var(--font-color-black-light);

      &:hover {
        transform: scale(90%);
      }
    }
  }

  .toggle {
    display: flex;
    align-items: center;
  }
}
</style>
